import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeDoc, deriveSurfaces, applySuggestion, resolveSuggestion } from '../utils/prdTickets';
import TicketCard, { SectionTable, renum, cellField } from '../components/prd/TicketCard';
import ReviewPanel from '../components/prd/ReviewPanel';

/**
 * PRDPage — PRD Builder. Type a short requirement; the proxy LLM drafts a PRD
 * in the house format (Goal, Background + mapping table + examples, numbered
 * Requirements, bulleted Acceptance Criteria); edit, request an AI Review
 * (suggestions you accept or reject), save, copy
 * (rich HTML so tables paste as tables into Docs/Confluence/Jira) or export .md.
 *
 *   GET    /proxy/prd                        -> { prds: [...] }
 *   GET    /proxy/prd/:id[?format=markdown|html] -> { prd } | markdown | html
 *   POST   /proxy/prd/generate { summary, meta?, images? } -> { prd }
 *   POST   /proxy/prd/:id/critique { updatedAt, instruction } -> { suggestions, basedOn }
 *
 * images: [{ mediaType, data }] (bare base64), from paste or upload; the
 * model reads them as source material (prd/generate.js).
 *   POST   /proxy/prd/:id (save; PUT is not CORS-allowed) -> { prd }
 *   DELETE /proxy/prd/:id                    -> { ok }
 */

// Host only — each call adds its own `/proxy` (see ChaosPage for why).
const PROXY_BASE = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');

const EMPTY_STATE = 'Describe a requirement and the tool drafts a full, structured PRD';

function errorMessage(status, body, scope = '') {
  const code = body && body.error;
  if (scope === 'review') {
    if (code === 'no_api_key') return "Review needs the proxy's Anthropic key. Ask whoever runs the proxy to set it.";
    if (code === 'llm_truncated') return 'The review ran out of room before finishing. Try again with a narrower focus in the box.';
    if (status === 404) {
      return body && body.resource === 'prd'
        ? 'This PRD no longer exists. Reload the page.'
        : 'Review is not available on this proxy yet. Reload in a minute.';
    }
  }
  if (code === 'stale_doc') return 'This PRD changed since you opened it. Reload it, then run Review again.';
  if (code === 'invalid_tickets') return 'This save had no tickets in it and was not stored. Reload the page and try again.';
  if (code === 'no_api_key') return 'LLM key not configured on the proxy (ANTHROPIC_API_KEY)';
  if (code === 'llm_daily_cap_exceeded') return (body && body.message) || 'Daily LLM token cap exceeded';
  if (code === 'missing_summary') return 'Enter a requirement summary first';
  if (body && body.message) return body.message;
  return `Request failed (${status})`;
}

async function api(path, opts, scope = '') {
  let res;
  try {
    res = await fetch(`${PROXY_BASE}/proxy/prd${path}`, opts);
  } catch (e) {
    throw new Error(`Network error: ${e.message}`);
  }
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch (_) { /* non-JSON error */ }
    throw new Error(errorMessage(res.status, body, scope));
  }
  return res;
}

const jsonOpts = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// Generate/enhance stream through the proxy's keepalive path: the status is
// always 200 and a failure arrives in the body as { ok:false, error } instead
// of an HTTP error code — so the prd must be checked for, not assumed.
async function readPrd(res) {
  const body = await res.json();
  if (body && body.prd) return body.prd;
  const err = String((body && body.error) || 'Generation failed');
  if (err.startsWith('llm_daily_cap_exceeded')) throw new Error(err);
  if (err === 'no_api_key') throw new Error('LLM key not configured on the proxy (ANTHROPIC_API_KEY)');
  if (err === 'llm_truncated') throw new Error('The model ran out of room before finishing the PRD. Try a narrower instruction.');
  throw new Error(err);
}

// Screenshots: downscaled in the browser to the model's useful resolution
// (1568px long edge) so a retina capture doesn't ship 10MB of base64.
const MAX_IMAGES = 4;
const MAX_EDGE = 1568;
const MAX_B64 = 5 * 1024 * 1024;

async function fileToImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`Could not read ${file.name || 'image'}`));
      el.src = url;
    });
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    // PNG keeps screenshot text crisp; fall back to JPEG if it is too big.
    let dataUrl = canvas.toDataURL('image/png');
    if (dataUrl.length * 0.99 > MAX_B64) dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const [head, data] = dataUrl.split(',');
    return { mediaType: head.slice(5, head.indexOf(';')), data, preview: dataUrl };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function useImages() {
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState(null);
  const add = useCallback(async (files) => {
    const list = Array.from(files || []).filter((f) => f.type && f.type.startsWith('image/'));
    if (!list.length) return;
    setImageError(null);
    try {
      const next = await Promise.all(list.map(fileToImage));
      setImages((cur) => {
        const all = [...cur, ...next];
        if (all.length > MAX_IMAGES) setImageError(`Up to ${MAX_IMAGES} screenshots; extras were dropped`);
        return all.slice(0, MAX_IMAGES);
      });
    } catch (e) { setImageError(e.message); }
  }, []);
  // Paste handler for text fields: claims the event only when it carries an image.
  const onPaste = useCallback((e) => {
    const files = Array.from((e.clipboardData && e.clipboardData.files) || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    e.preventDefault();
    add(files);
  }, [add]);
  const remove = (i) => setImages((cur) => cur.filter((_, j) => j !== i));
  const clear = () => setImages([]);
  // Wire shape: preview stays in the browser.
  const payload = () => images.map(({ mediaType, data }) => ({ mediaType, data }));
  return { images, imageError, setImageError, add, onPaste, remove, clear, payload };
}

function ImageAttach({ ctl, disabled }) {
  const input = useRef(null);
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Button
          size="small"
          startIcon={<ImageOutlinedIcon />}
          onClick={() => input.current && input.current.click()}
          disabled={disabled || ctl.images.length >= MAX_IMAGES}
        >
          Attach screenshot
        </Button>
        <Typography variant="caption" color="text.secondary">or paste one into the text box (up to {MAX_IMAGES})</Typography>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          multiple
          hidden
          onChange={(e) => { ctl.add(e.target.files); e.target.value = ''; }}
        />
      </Stack>
      {ctl.imageError && <Alert severity="warning" sx={{ mt: 1 }} onClose={() => ctl.setImageError(null)}>{ctl.imageError}</Alert>}
      {ctl.images.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
          {ctl.images.map((im, i) => (
            <Box key={i} sx={{ position: 'relative', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <img src={im.preview} alt={`screenshot ${i + 1}`} style={{ display: 'block', height: 72, maxWidth: 140, objectFit: 'cover' }} />
              <IconButton
                size="small"
                aria-label="remove screenshot"
                onClick={() => ctl.remove(i)}
                disabled={disabled}
                sx={{ position: 'absolute', top: 2, right: 2, p: 0.25, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function relTime(iso) {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const normalize = normalizeDoc;

// Old -> new value mapping in Background (e.g. sub category | current IU | new IU).
function MappingTable({ table, onChange }) {
  const { columns, rows } = table;
  if (!columns.length) {
    return (
      <Button size="small" startIcon={<AddIcon />} sx={{ mb: 1 }}
        onClick={() => onChange({ columns: ['Case', 'Current', 'New'], rows: [['', '', '']] })}>
        Add mapping table
      </Button>
    );
  }
  const setCol = (c, v) => onChange({ columns: columns.map((x, j) => (j === c ? v : x)), rows });
  const setCell = (r, c, v) => onChange({ columns, rows: rows.map((row, i) => (i === r ? row.map((x, j) => (j === c ? v : x)) : row)) });
  return (
    <Box sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c, j) => (
              <TableCell key={j}>
                <TextField {...cellField} value={c} onChange={(e) => setCol(j, e.target.value)}
                  InputProps={{ ...cellField.InputProps, sx: { fontSize: 13, fontWeight: 700 } }} />
              </TableCell>
            ))}
            <TableCell sx={{ width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((_, j) => (
                <TableCell key={j}>
                  <TextField {...cellField} value={row[j] || ''} onChange={(e) => setCell(i, j, e.target.value)} />
                </TableCell>
              ))}
              <TableCell>
                <IconButton size="small" aria-label="delete mapping row" onClick={() => onChange({ columns, rows: rows.filter((_, k) => k !== i) })}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ columns, rows: [...rows, columns.map(() => '')] })}>Add row</Button>
        <Button size="small" color="error" onClick={() => onChange({ columns: [], rows: [] })}>Remove table</Button>
      </Stack>
    </Box>
  );
}

function CreateForm({ onCreated }) {
  const [summary, setSummary] = useState('');
  const [meta, setMeta] = useState({ sites: '', platforms: '', priority: '', targetRelease: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const shots = useImages();

  const generate = async () => {
    if (!summary.trim() && !shots.images.length) { setError('Enter a requirement summary or attach a screenshot first'); return; }
    setBusy(true);
    setError(null);
    try {
      const m = {};
      Object.keys(meta).forEach((k) => { if (meta[k].trim()) m[k] = meta[k].trim(); });
      const res = await api('/generate', jsonOpts('POST', {
        summary: summary.trim() || 'Draft the PRD from the attached screenshot(s).',
        meta: m,
        images: shots.payload(),
      }));
      onCreated(await readPrd(res));
      shots.clear();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const metaField = (key, label) => (
    <TextField
      key={key}
      size="small"
      label={label}
      value={meta[key]}
      onChange={(e) => setMeta({ ...meta, [key]: e.target.value })}
      sx={{ minWidth: 160, flex: 1 }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>New PRD</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{EMPTY_STATE}.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TextField
        label="Describe the requirement…"
        placeholder="update ius in article pages for outkick — new path /15104292/outkick/article, web + apps"
        multiline
        minRows={4}
        fullWidth
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        onPaste={shots.onPaste}
        sx={{ mb: 1 }}
      />
      <Box sx={{ mb: 2 }}><ImageAttach ctl={shots} disabled={busy} /></Box>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {metaField('sites', 'Sites')}
        {metaField('platforms', 'Platforms')}
        {metaField('priority', 'Priority')}
        {metaField('targetRelease', 'Target Release')}
      </Stack>
      <Button
        variant="contained"
        onClick={generate}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {busy ? 'Generating…' : 'Generate'}
      </Button>
    </Paper>
  );
}

// House rules appended to every generation/enhance prompt server-side.
// Edited here so a new editorial rule never needs a code change or deploy.
function GuidelinesPanel() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await api('/guidelines');
        setText((await res.json()).text || '');
        setLoaded(true);
      } catch (e) { setError(e.message); }
    }
  };

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await api('/guidelines', jsonOpts('POST', { text }));
      setText((await res.json()).text || '');
      setSaved(true);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Button size="small" fullWidth variant="outlined" onClick={toggle}>
        {open ? 'Hide PRD guidelines' : 'PRD guidelines'}
      </Button>
      {open && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            House rules every generated PRD follows. One rule per line, e.g. "Always include a rollback section."
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSaved(false)}>Saved — applies to the next generation</Alert>}
          <TextField
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            size="small"
            value={text}
            onChange={(e) => { setText(e.target.value); setSaved(false); }}
            placeholder={'Never include vendors I did not name.\nAcceptance criteria must be checkable from captured traffic.'}
          />
          <Button size="small" variant="contained" onClick={save} disabled={saving} sx={{ mt: 1 }}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
            Save guidelines
          </Button>
        </Paper>
      )}
    </Box>
  );
}

function Editor({ doc, dirty, setDoc, onSaved, onDeleted }) {
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [notice, setNotice] = useState(null);

  const edit = (patch) => setDoc({ ...doc, ...patch });
  // A ticket edit re-derives analysis.surfaces so the chip list follows the cards.
  const editTicket = (i, patch) => {
    const tickets = doc.tickets.map((t, j) => (j === i ? { ...t, ...patch } : t));
    const surfaces = deriveSurfaces(tickets);
    setDoc({ ...doc, tickets, analysis: { ...(doc.analysis || {}), surfaces, surface: surfaces[0] } });
  };

  const save = async () => {
    const res = await api(`/${encodeURIComponent(doc.id)}`, jsonOpts('POST', doc));
    const { prd } = await res.json();
    onSaved(normalize(prd));
    return prd;
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try { await save(); } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const getMarkdown = async () => {
    if (dirty) await save();
    const res = await api(`/${encodeURIComponent(doc.id)}?format=markdown`);
    return res.text();
  };

  // Rich copy: Docs/Confluence/Jira paste the HTML (real tables, bullets);
  // plain-text targets get the markdown.
  const onCopy = async () => {
    setExporting(true);
    setError(null);
    try {
      const md = await getMarkdown();
      const html = await (await api(`/${encodeURIComponent(doc.id)}?format=html`)).text();
      if (window.ClipboardItem && navigator.clipboard.write) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([md], { type: 'text/plain' }),
        })]);
        setNotice('Copied. Paste into Google Docs, Confluence or Jira');
      } else {
        await navigator.clipboard.writeText(md);
        setNotice('Markdown copied');
      }
    } catch (e) { setError(e.message); } finally { setExporting(false); }
  };

  // Same clipboard path as onCopy, scoped to one ticket (1-based n). An unsaved
  // edit is saved first so the server renders what the card shows.
  const onCopyTicket = async (n) => {
    setExporting(true);
    setError(null);
    try {
      if (dirty) await save();
      const base = `/${encodeURIComponent(doc.id)}`;
      const md = await (await api(`${base}?format=markdown&ticket=${n}`)).text();
      const html = await (await api(`${base}?format=html&ticket=${n}`)).text();
      if (window.ClipboardItem && navigator.clipboard.write) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([md], { type: 'text/plain' }),
        })]);
        setNotice('Copied. Paste into Google Docs, Confluence or Jira');
      } else {
        await navigator.clipboard.writeText(md);
        setNotice('Markdown copied');
      }
    } catch (e) { setError(e.message); } finally { setExporting(false); }
  };

  const onDownload = async () => {
    setExporting(true);
    setError(null);
    try {
      const md = await getMarkdown();
      const blob = new Blob([md], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PRD - ${(doc.title || 'Untitled').replace(/[\\/:*?"<>|]/g, '-')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); } finally { setExporting(false); }
  };

  // Review: one billed call. Keepalive path, so status is 200 and a failure
  // arrives as { ok:false, error }. The doc is untouched until an Accept.
  const onReview = async () => {
    if (dirty) return;
    setReviewing(true);
    setError(null);
    try {
      const res = await api(
        `/${encodeURIComponent(doc.id)}/critique`,
        jsonOpts('POST', { updatedAt: doc.updatedAt, instruction: instruction.trim() }),
        'review',
      );
      const body = await res.json();
      if (!body || !Array.isArray(body.suggestions)) {
        const err = String((body && body.error) || 'Review failed');
        throw new Error(err.startsWith('llm_daily_cap_exceeded') ? err : errorMessage(200, { ...body, error: err }, 'review'));
      }
      setSuggestions(body.suggestions);
    } catch (e) { setError(e.message); } finally { setReviewing(false); }
  };

  // Accept: apply to the in-memory doc, save through the normal path (no LLM
  // call), adopt the server's doc (new updatedAt) only on success.
  const onAccept = async (s) => {
    const next = applySuggestion(doc, s);
    if (next === doc) return;
    setAccepting(true);
    setError(null);
    try {
      const res = await api(`/${encodeURIComponent(doc.id)}`, jsonOpts('POST', next));
      const { prd } = await res.json();
      onSaved(normalize(prd));
      setSuggestions((cur) => cur.filter((x) => x !== s));
    } catch (e) { setError(e.message); } finally { setAccepting(false); }
  };

  const onDelete = async () => {
    setError(null);
    try {
      await api(`/${encodeURIComponent(doc.id)}`, { method: 'DELETE' });
      onDeleted(doc.id);
    } catch (e) { setError(e.message); }
  };

  const busy = saving || exporting || reviewing;
  const addRow = (key) => edit({ [key]: renum([...doc[key], { num: 0, details: '' }]) });
  const setRows = (key) => (rows) => edit({ [key]: rows });

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{ position: 'sticky', top: 0, zIndex: 2, p: 1, mb: 2, bgcolor: 'background.paper' }}
      >
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Button variant="contained" size="small" onClick={onSave} disabled={!dirty || busy}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button size="small" onClick={() => setReviewOpen((o) => !o)} disabled={busy || dirty}
            title={dirty ? 'Save first' : undefined}>Review</Button>
          <Button size="small" onClick={onCopy} disabled={busy}>Copy</Button>
          <Button size="small" onClick={onDownload} disabled={busy}>Download .md</Button>
          {confirmDel ? (
            <>
              <Typography variant="caption">Delete this PRD?</Typography>
              <Button size="small" color="error" onClick={onDelete}>Yes, delete</Button>
              <Button size="small" onClick={() => setConfirmDel(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="small" color="error" onClick={() => setConfirmDel(true)} disabled={busy}>Delete</Button>
          )}
          <Box sx={{ flex: 1 }} />
          {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <ReviewPanel
        open={reviewOpen}
        instruction={instruction}
        onInstruction={setInstruction}
        onRun={onReview}
        running={reviewing}
        disabled={dirty || busy}
        suggestions={suggestions}
        onReject={(i) => setSuggestions((cur) => cur.filter((_, j) => j !== i))}
        onAccept={onAccept}
        resolve={(s) => resolveSuggestion(doc, s)}
        accepting={accepting || busy}
      />

      <TextField
        variant="standard"
        fullWidth
        placeholder="Untitled PRD"
        value={doc.title}
        onChange={(e) => edit({ title: e.target.value })}
        InputProps={{ sx: { fontSize: '1.5rem', fontWeight: 600 } }}
        sx={{ mb: 2 }}
      />

      {doc.analysis && (doc.analysis.system || doc.analysis.surface || doc.analysis.shape) && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {doc.analysis.system && <Chip size="small" color="primary" variant="outlined" label={`System: ${doc.analysis.system}`} />}
          {(Array.isArray(doc.analysis.surfaces) && doc.analysis.surfaces.length ? doc.analysis.surfaces : [doc.analysis.surface])
            .filter(Boolean)
            .map((s) => <Chip key={s} size="small" variant="outlined" label={`Surface: ${s}`} />)}
          {doc.analysis.shape && <Chip size="small" variant="outlined" label={`Shape: ${doc.analysis.shape}`} />}
          {Array.isArray(doc.analysis.unknowns) && doc.analysis.unknowns.length > 0 && (
            <Chip size="small" color="warning" variant="outlined" label={`${doc.analysis.unknowns.length} unknown${doc.analysis.unknowns.length > 1 ? 's' : ''}`} />
          )}
        </Stack>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Goal</Typography>
      <TextField multiline minRows={2} fullWidth value={doc.goals} onChange={(e) => edit({ goals: e.target.value })} sx={{ mb: 2 }} />

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Background</Typography>
        <TextField multiline minRows={3} fullWidth value={doc.background} onChange={(e) => edit({ background: e.target.value })} sx={{ mb: 1.5 }} />
        <MappingTable table={doc.backgroundTable} onChange={(t) => edit({ backgroundTable: t })} />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <TextField size="small" variant="standard" placeholder="Example APIs" value={doc.examples.label}
            onChange={(e) => edit({ examples: { ...doc.examples, label: e.target.value } })}
            InputProps={{ sx: { fontSize: 13, fontWeight: 600 } }} sx={{ width: 200 }} />
        </Stack>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {doc.examples.items.map((x, i) => (
            <Box component="li" key={i}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField {...cellField} value={x}
                  onChange={(e) => edit({ examples: { ...doc.examples, items: doc.examples.items.map((y, j) => (j === i ? e.target.value : y)) } })} />
                <IconButton size="small" aria-label="delete example"
                  onClick={() => edit({ examples: { ...doc.examples, items: doc.examples.items.filter((_, j) => j !== i) } })}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Box>
        <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5 }}
          onClick={() => edit({ examples: { label: doc.examples.label || 'Example APIs', items: [...doc.examples.items, ''] } })}>
          Add example
        </Button>
      </Paper>

      {doc.tickets.map((t, i) => (
        <TicketCard
          key={i}
          ticket={t}
          index={i}
          total={doc.tickets.length}
          busy={busy}
          onChange={(patch) => editTicket(i, patch)}
          onCopy={onCopyTicket}
        />
      ))}

      {(doc.assumptions.length > 0 || doc.questions.length > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary">Review notes (not part of the copied/exported PRD)</Typography>
          {doc.questions.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => edit({ questions: [] })}>
              <Typography variant="subtitle2">Open questions</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {doc.questions.map((q, i) => <li key={i}>{q}</li>)}
              </Box>
            </Alert>
          )}
          <SectionTable
            title="Assumptions"
            caption="Details the generator inferred. Confirm them in the PRD text or delete them."
            sx={{ bgcolor: 'action.hover' }}
            rows={doc.assumptions}
            columns={{ numLabel: '#' }}
            onChange={setRows('assumptions')}
            onAdd={() => addRow('assumptions')}
          />
        </Box>
      )}
    </Box>
  );
}

export default function PRDPage() {
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(null); // null = create form
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [rowDeleteId, setRowDeleteId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api('');
      const data = await res.json();
      setList(data.prds || []);
      setListError(null);
    } catch (e) {
      setListError(e.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const open = async (id) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setLoadingDoc(true);
    setListError(null);
    try {
      const res = await api(`/${encodeURIComponent(id)}`);
      const { prd } = await res.json();
      setDoc(normalize(prd));
      setSelectedId(id);
      setDirty(false);
    } catch (e) {
      setListError(e.message);
    } finally {
      setLoadingDoc(false);
    }
  };

  const startNew = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setSelectedId(null);
    setDoc(null);
    setDirty(false);
  };

  const loadClean = (prd) => {
    setDoc(normalize(prd));
    setSelectedId(prd.id);
    setDirty(false);
    refresh();
  };

  const deleteRow = async (id) => {
    setRowDeleteId(null);
    try {
      await api(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (id === selectedId) { setSelectedId(null); setDoc(null); setDirty(false); }
      refresh();
    } catch (e) {
      setListError(e.message);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, p: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
        <Button variant="contained" fullWidth onClick={startNew} startIcon={<AddIcon />} sx={{ mb: 1.5 }}>
          New PRD
        </Button>
        <GuidelinesPanel />
        {listError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setListError(null)}>{listError}</Alert>}
        {listLoading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
        ) : list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No saved PRDs yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {list.map((p) => (
              <Paper
                key={p.id}
                variant="outlined"
                onClick={() => open(p.id)}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  borderColor: p.id === selectedId ? 'primary.main' : undefined,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.title || 'Untitled PRD'}</Typography>
                    <Typography variant="caption" color="text.secondary">{relTime(p.updatedAt)}</Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {p.sites && <Chip size="small" label={p.sites} />}
                      {p.priority && <Chip size="small" variant="outlined" label={p.priority} />}
                    </Stack>
                  </Box>
                  {rowDeleteId === p.id ? (
                    <Stack onClick={(e) => e.stopPropagation()} alignItems="flex-end">
                      <Button size="small" color="error" onClick={() => deleteRow(p.id)}>Delete?</Button>
                      <Button size="small" onClick={() => setRowDeleteId(null)}>No</Button>
                    </Stack>
                  ) : (
                    <IconButton
                      size="small"
                      aria-label="delete PRD"
                      onClick={(e) => { e.stopPropagation(); setRowDeleteId(p.id); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {loadingDoc ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : doc ? (
          <Editor
            key={doc.id}
            doc={doc}
            dirty={dirty}
            setDoc={(d) => { setDoc(d); setDirty(true); }}
            onSaved={(d) => { setDoc(d); setDirty(false); refresh(); }}
            onDeleted={() => { setSelectedId(null); setDoc(null); setDirty(false); refresh(); }}
          />
        ) : (
          <CreateForm onCreated={loadClean} />
        )}
      </Box>
    </Box>
  );
}
